// What a standings table says beyond each team's own line (H1, B-387): its
// results in order, its all-play record, and how far it is from the playoff
// line.
//
// RESULTS are the games against somebody, as the streak counts them: a
// regular-season game with two teams in it, a win, a loss or a tie by the
// stored winner (N1). A median game counts in the record and nowhere else
// (PLAN-2026-10-16 §2), and a week against the field has nobody to beat.
//
// THE ALL-PLAY RECORD is each regular week's score against every other
// team's that week (`fieldResults`), weeks against the field included: the
// record the standings writer ranks an all-play league by and breaks a
// head-to-head tie with (`all-play-record`). It says how a team would stand
// had it played everybody every week, which a schedule's luck cannot move.
//
// GAMES FROM THE LINE are the wild-card column a baseball table carries:
// a team in the field is that many games clear of the first team out, a team
// outside it that many short of the last team in. Under division winners
// first the leaders are in by their divisions and the race is for the seats
// left, so it is measured among the rest.
//
// ON THE WIRE, NOT A CLIENT'S ARITHMETIC (CLAUDE.md, the backend is a
// product): a second frontend reading the table would otherwise write each
// of these again.

import { fieldResults } from '../matchup/field';

/** A finished game, as a store holds it, with the winner it worked out (`winnerOf`). */
export interface TableGame {
  period: number;
  /** 'regular', 'median', 'playoff', 'consolation'. */
  kind: string;
  /** Which of the week's games (Q6): 1 for its only or first, 2 for its second. Absent is 1. */
  game?: number;
  homeTeamId: string | null;
  /** Null for a week against the field. */
  awayTeamId: string | null;
  homeScore: number | null;
  awayScore: number | null;
  won: 'home' | 'away' | 'tie';
}

export type GameResult = 'W' | 'L' | 'T';

export interface TableRecord {
  wins: number;
  losses: number;
  ties: number;
}

export interface TableLine {
  /** Each regular-season game against somebody, oldest first. */
  results: GameResult[];
  /** Null before the team has a finished regular week. */
  allPlay: TableRecord | null;
}

/** Every team's results and all-play record, from the season's finished games in any order. */
export function tableLines(finals: readonly TableGame[]): Map<string, TableLine> {
  // A week's first game before its second (Q6), as the streak reads them.
  const games = [...finals].sort((a, b) => a.period - b.period || (a.game ?? 1) - (b.game ?? 1));
  const lines = new Map<string, TableLine>();
  const lineOf = (teamId: string) => {
    let line = lines.get(teamId);
    if (line === undefined) {
      line = { results: [], allPlay: null };
      lines.set(teamId, line);
    }
    return line;
  };
  const weeks = new Map<number, { teamId: string; score: number }[]>();
  // A TEAM'S WEEK ONCE in the all-play record, however many games it was
  // set against (Q6).
  const counted = new Set<string>();
  const scored = (period: number, teamId: string | null, score: number | null) => {
    if (teamId === null || score === null || counted.has(`${period}|${teamId}`)) return;
    counted.add(`${period}|${teamId}`);
    weeks.set(period, [...(weeks.get(period) ?? []), { teamId, score }]);
  };
  for (const g of games) {
    if (g.kind !== 'regular') continue;
    scored(g.period, g.homeTeamId, g.homeScore);
    scored(g.period, g.awayTeamId, g.awayScore);
    if (g.homeTeamId === null || g.awayTeamId === null) continue;
    const home = lineOf(g.homeTeamId);
    const away = lineOf(g.awayTeamId);
    home.results.push(g.won === 'home' ? 'W' : g.won === 'away' ? 'L' : 'T');
    away.results.push(g.won === 'away' ? 'W' : g.won === 'home' ? 'L' : 'T');
  }
  for (const week of weeks.values()) {
    for (const [teamId, r] of fieldResults(week)) {
      const line = lineOf(teamId);
      const was = line.allPlay ?? { wins: 0, losses: 0, ties: 0 };
      line.allPlay = {
        wins: was.wins + r.wins,
        losses: was.losses + r.losses,
        ties: was.ties + r.ties,
      };
    }
  }
  return lines;
}

/**
 * Each row with its results and all-play record (`tableLines`): none and
 * null for a team without a finished game.
 */
export function withTableLines<R extends { team: { id: string } }>(
  rows: readonly R[],
  finals: readonly TableGame[],
): (R & TableLine)[] {
  const lines = tableLines(finals);
  return rows.map((r) => {
    const line = lines.get(r.team.id);
    return { ...r, results: line?.results ?? [], allPlay: line?.allPlay ?? null };
  });
}

/**
 * Each row with its games from the playoff line (`StandingsRow.gamesFromLine`):
 * above zero clear of it, below zero short of it, zero level with the team
 * on the other side, which the tiebreakers put there. Null for every row
 * while the table is unranked or has no line (no bracket, or room for
 * everybody), and for a division leader seeded by its division. Null too
 * where nobody has a record to measure it in: a table nobody has played,
 * and a race on points (total points, elimination), whose places are its
 * points and whose records are all 0–0.
 */
export function withPlayoffLine<
  R extends {
    rank: number | null;
    seed: number | null;
    wins: number;
    losses: number;
    ties: number;
    team: { id: string };
    divisionId: string | null;
  },
>(
  rows: readonly R[],
  playoffs: { teamCount: number; seeding: 'standings' | 'division-winners-first' },
): (R & { gamesFromLine: number | null })[] {
  const none = () => rows.map((r) => ({ ...r, gamesFromLine: null }));
  if (rows.some((r) => r.rank === null)) return none();
  if (playoffs.teamCount <= 0 || playoffs.teamCount >= rows.length) return none();
  if (rows.every((r) => r.wins + r.losses + r.ties === 0)) return none();
  const table = [...rows].sort((a, b) => a.rank! - b.rank!);
  // THE LEADERS, under division winners first: the best-ranked team in each
  // division, in by its division and out of the race for the seats left.
  const leaders = new Set<string>();
  if (playoffs.seeding === 'division-winners-first') {
    const seen = new Set<string>();
    for (const r of table) {
      if (r.divisionId === null || seen.has(r.divisionId)) continue;
      seen.add(r.divisionId);
      leaders.add(r.team.id);
    }
  }
  const race = table.filter((r) => !leaders.has(r.team.id));
  const inside = race.filter((r) => r.seed !== null);
  const outside = race.filter((r) => r.seed === null);
  if (inside.length === 0 || outside.length === 0) return none();
  const lastIn = inside.reduce((m, r) => (r.seed! > m.seed! ? r : m));
  const firstOut = outside[0]!;
  const net = (r: { wins: number; losses: number }) => r.wins - r.losses;
  return rows.map((r) => ({
    ...r,
    gamesFromLine: leaders.has(r.team.id)
      ? null
      : (net(r) - net(r.seed !== null ? firstOut : lastIn)) / 2,
  }));
}
